/**
 * Human-readable math expressions for reward portions.
 *
 * Given a structured `RewardPortion` produced by the backend rules engine,
 * we derive an inline math chip + a longer expression that explains exactly
 * how the engine arrived at the rupee value.
 *
 * The backend already gives us:
 *   - eligible_amount_inr  → the spend amount eligible under the rule
 *   - reward_raw           → raw units earned (points / miles / INR)
 *   - reward_value_inr     → INR value of that raw reward
 *   - currency             → "RP" / "EDGE Miles" / "INR" etc
 *   - kind                 → 'primary' | 'spillover' | 'milestone' | 'tier'
 *
 * We don't have the explicit `rate_per_100` or `point_value_inr` fields, but
 * both can be derived deterministically from the three quantities above —
 * so the displayed expression matches the engine's actual computation.
 */
import { formatINR } from '@/lib/format';

const round = (n, digits = 2) => {
  if (!Number.isFinite(n)) return 0;
  const f = Math.pow(10, digits);
  return Math.round(n * f) / f;
};

const isCashbackPortion = (portion) => {
  // The engine explicitly sets currency='INR' for percent_cashback rules.
  // We rely on that (no fuzzy raw==inr matching, because a 1:1 point value
  // would otherwise be misidentified as cashback).
  const cur = (portion?.currency || '').toUpperCase();
  return cur === 'INR' || cur === '₹';
};

/**
 * Build a "step list" of multiplication factors for the portion.
 *
 * Returns an array of { label, value } pairs that, when joined with × signs,
 * produces the full computation. Example output:
 *   [
 *     { label: "spend",            value: "₹15,000"        },
 *     { label: "5 RP / ₹150",      value: "= 500 RP"       },
 *     { label: "₹1.00 / RP",       value: "= ₹500.00"      },
 *   ]
 */
export function derivePortionSteps(portion) {
  if (!portion) return [];
  const eligible = portion.eligible_amount_inr || 0;
  const raw = portion.reward_raw || 0;
  const inr = portion.reward_value_inr || 0;

  // Milestones / tiers are flat bonuses, not multiplicative — eligible is 0.
  if (portion.kind === 'milestone' || portion.kind === 'tier') {
    return [
      {
        label: portion.kind === 'milestone' ? 'Milestone bonus' : 'Tier bonus',
        value: formatINR(inr),
        meta: portion.rule_label || portion.note || '',
      },
    ];
  }

  // Defensive: if there's nothing to show, render the rupee total directly.
  if (eligible <= 0) {
    return [{ label: 'Reward', value: formatINR(inr) }];
  }

  // ── Cashback path: a flat percent of eligible spend.
  if (isCashbackPortion(portion)) {
    const pct = round((inr / eligible) * 100, 2);
    return [
      { label: 'Eligible spend', value: formatINR(eligible) },
      { label: `× ${pct}% cashback`, value: `= ${formatINR(inr)}` },
    ];
  }

  // ── Points / miles path: derive rate per ₹100 + point value in ₹.
  const ratePer100 = raw > 0 ? round((raw / eligible) * 100, 2) : 0;
  const pointValue = raw > 0 ? round(inr / raw, 4) : 0;
  const currency = portion.currency || 'pts';
  const ratePer100Str = ratePer100.toLocaleString('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: ratePer100 % 1 === 0 ? 0 : 2,
  });
  const rawStr = raw.toLocaleString('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
  const pointValueStr = `₹${pointValue.toLocaleString('en-IN', { maximumFractionDigits: 4, minimumFractionDigits: 2 })}`;

  const steps = [
    { label: 'Eligible spend', value: formatINR(eligible) },
    { label: `× ${ratePer100Str} ${currency} / ₹100`, value: `= ${rawStr} ${currency}` },
  ];
  // Only show the point-value conversion step if the unit value is non-trivial
  // (e.g. ₹0.25/RP, ₹0.40/EDGE Mile). When 1 RP = ₹1.00 the conversion adds
  // no information and clutters the chip.
  if (Math.abs(pointValue - 1) > 0.005) {
    steps.push({ label: `× ${pointValueStr} / ${currency}`, value: `= ${formatINR(inr)}` });
  } else {
    // Annotate the final equality so the reader still sees the rupee value.
    steps[steps.length - 1] = {
      label: steps[steps.length - 1].label,
      value: `= ${rawStr} ${currency} (≈ ${formatINR(inr)})`,
    };
  }
  return steps;
}

/**
 * Single-line expression suitable for inline display next to a result.
 * Example: "₹15,000 × 5 RP/₹100 × ₹1.00/RP = ₹500.00"
 */
export function inlineExpression(portion) {
  const steps = derivePortionSteps(portion);
  if (!steps.length) return '';
  // We render steps as "operand × operand × … = total" where the last step's
  // value is the equals-sign segment.
  const parts = [];
  steps.forEach((s, i) => {
    const isLast = i === steps.length - 1;
    if (i === 0) {
      // First step is just the operand.
      parts.push(s.value);
    } else if (isLast) {
      // Last step contains the equals sign already.
      parts.push(s.label.replace(/^×\s*/, '× '));
      parts.push(s.value); // already starts with "= "
    } else {
      parts.push(s.label.replace(/^×\s*/, '× '));
    }
  });
  return parts.join(' ');
}

/**
 * Title chip for the portion kind, used to label each sub-row.
 * Returns { label, tone } where `tone` is a tailwind colour token name.
 */
export function portionChip(kind) {
  switch (kind) {
    case 'primary':   return { label: 'Primary',   tone: 'accent' };
    case 'spillover': return { label: 'Spillover', tone: 'amber'  };
    case 'milestone': return { label: 'Milestone', tone: 'sky'    };
    case 'tier':      return { label: 'Tier',      tone: 'violet' };
    default:          return { label: kind || '—', tone: 'muted'  };
  }
}

/**
 * Compute a card-level summary string.
 * Example: "₹15,000 spend → ₹500.00 reward (3.33% effective)"
 */
export function summaryLine({ amount, reward_value_inr, effective_rate_pct }) {
  if (!amount || amount <= 0) return '';
  return `${formatINR(amount)} spend → ${formatINR(reward_value_inr || 0)} reward (${(effective_rate_pct || 0).toFixed(2)}% effective)`;
}
